// frontend/src/settings/LinkSettings.js - Supabase完全対応版
import React, { useState, useEffect } from "react";
import { supabase } from "../supabase"
import { useOrganization } from "../contexts/OrganizationContext";
import { Link as LinkIcon, Save, ExternalLink, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const LinkSettings = () => {
  const { currentOrganization } = useOrganization();
  const [attendanceUrl, setAttendanceUrl] = useState("");
  const [manualUrl, setManualUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (currentOrganization?.id) fetchSettings();
  }, [currentOrganization?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log("🔍 設定取得開始（Supabase）...");

      // Supabaseから設定を取得（組織ごと）
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .eq('organization_id', currentOrganization.id)
        .in('key', ['attendance_url', 'manual_url']);

      if (error) {
        console.error("❌ 設定取得エラー:", error);
        throw error;
      }

      console.log("✅ 設定取得成功:", data);

      // データをstateにセット
      if (data) {
        data.forEach(setting => {
          if (setting.key === 'attendance_url') {
            setAttendanceUrl(setting.value || "");
          } else if (setting.key === 'manual_url') {
            setManualUrl(setting.value || "");
          }
        });
      }
    } catch (error) {
      console.error("❌ 設定取得エラー:", error);
      setMessage("設定の取得に失敗しました: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");

      // URL形式チェック
      if (attendanceUrl && !attendanceUrl.startsWith("http")) {
        setMessage("出勤退勤URLは http:// または https:// で始まる必要があります");
        return;
      }
      if (manualUrl && !manualUrl.startsWith("http")) {
        setMessage("手順書URLは http:// または https:// で始まる必要があります");
        return;
      }

      console.log("💾 設定保存開始（Supabase）...");

      if (!currentOrganization?.id) {
        throw new Error('組織情報が読み込まれていません');
      }

      // 出勤退勤URLを保存（upsert）— 組織ごとに保存
      const { error: attendanceError } = await supabase
        .from('settings')
        .upsert({
          organization_id: currentOrganization.id,
          key: 'attendance_url',
          value: attendanceUrl,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organization_id,key'
        });

      if (attendanceError) {
        console.error("❌ 出勤退勤URL保存エラー:", attendanceError);
        throw attendanceError;
      }

      // 手順書URLを保存（upsert）— 組織ごとに保存
      const { error: manualError } = await supabase
        .from('settings')
        .upsert({
          organization_id: currentOrganization.id,
          key: 'manual_url',
          value: manualUrl,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organization_id,key'
        });

      if (manualError) {
        console.error("❌ 手順書URL保存エラー:", manualError);
        throw manualError;
      }

      console.log("✅ 設定保存成功");
      setMessage("✅ 保存しました");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("❌ 保存エラー:", error);
      setMessage("❌ 保存に失敗しました: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const testUrl = (url, type) => {
    if (!url) {
      alert(`${type}のURLが設定されていません`);
      return;
    }
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <Link to="/settings" style={styles.backButton}>
          <ArrowLeft size={20} />
          <span>設定に戻る</span>
        </Link>
        <h1 style={styles.title}>
          <LinkIcon size={28} style={{ marginRight: "12px" }} />
          リンク設定
        </h1>
        <p style={styles.description}>
          ホーム画面に表示するボタンのリンク先を設定します
        </p>
      </div>

      {/* メッセージ表示 */}
      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.includes("✅") ? "#d4edda" : "#f8d7da",
          color: message.includes("✅") ? "#155724" : "#721c24",
          borderColor: message.includes("✅") ? "#c3e6cb" : "#f5c6cb",
        }}>
          {message}
        </div>
      )}

      {/* 設定フォーム */}
      <div style={styles.formContainer}>
        {/* 出勤退勤システムURL */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <div style={styles.labelHeader}>
              <span>出勤退勤システムURL</span>
              {attendanceUrl && (
                <button
                  onClick={() => testUrl(attendanceUrl, "出勤退勤")}
                  style={styles.testButton}
                >
                  <ExternalLink size={14} />
                  テスト
                </button>
              )}
            </div>
          </label>
          <input
            type="url"
            value={attendanceUrl}
            onChange={(e) => setAttendanceUrl(e.target.value)}
            placeholder="https://example.com/attendance"
            style={styles.input}
          />
          <p style={styles.hint}>
            出勤・退勤を記録するシステムのURLを入力してください
            <br />
            例: タイムカードシステム、勤怠管理システムなど
          </p>
        </div>

        {/* 手順書URL */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <div style={styles.labelHeader}>
              <span>手順書・マニュアルURL</span>
              {manualUrl && (
                <button
                  onClick={() => testUrl(manualUrl, "手順書")}
                  style={styles.testButton}
                >
                  <ExternalLink size={14} />
                  テスト
                </button>
              )}
            </div>
          </label>
          <input
            type="url"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://example.com/manual"
            style={styles.input}
          />
          <p style={styles.hint}>
            手順書やマニュアルを閲覧できるURLを入力してください
            <br />
            例: Google Drive、Notion、社内Wikiなど
          </p>
        </div>

        {/* 保存ボタン */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            ...styles.saveButton,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          <Save size={20} />
          {saving ? "保存中..." : "保存"}
        </button>
      </div>

      {/* 使用例 */}
      <div style={styles.exampleContainer}>
        <h3 style={styles.exampleTitle}>📱 使用例</h3>
        <p style={styles.exampleText}>
          設定したURLは、スタッフのホーム画面に以下のように表示されます：
        </p>
        <div style={styles.exampleButtons}>
          <div style={styles.exampleButton}>
            <div style={styles.exampleIcon}>🕐</div>
            <div>出勤退勤</div>
            <div style={styles.exampleSubtext}>タイムカード</div>
          </div>
          <div style={styles.exampleButton}>
            <div style={styles.exampleIcon}>📖</div>
            <div>手順書</div>
            <div style={styles.exampleSubtext}>マニュアル</div>
          </div>
        </div>
        <p style={styles.exampleNote}>
          ※ URLが未設定の場合、ボタンをクリックすると設定を促すメッセージが表示されます
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "2rem",
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  header: {
    marginBottom: "2rem",
  },
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#666",
    textDecoration: "none",
    fontSize: "0.9rem",
    marginBottom: "1rem",
    transition: "color 0.2s",
    cursor: "pointer",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "0.5rem",
    display: "flex",
    alignItems: "center",
  },
  description: {
    fontSize: "1rem",
    color: "#64748b",
    margin: 0,
  },
  loading: {
    textAlign: "center",
    padding: "3rem",
    color: "#64748b",
  },
  message: {
    padding: "1rem",
    borderRadius: "8px",
    marginBottom: "1.5rem",
    border: "1px solid",
    fontWeight: "500",
  },
  formContainer: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "2rem",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    marginBottom: "2rem",
  },
  formGroup: {
    marginBottom: "2rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontWeight: "600",
    color: "#334155",
    fontSize: "1rem",
  },
  labelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  testButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    padding: "0.4rem 0.8rem",
    fontSize: "0.85rem",
    color: "#3b82f6",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  input: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: "0.85rem",
    color: "#64748b",
    marginTop: "0.5rem",
    lineHeight: "1.5",
  },
  saveButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    width: "100%",
    padding: "1rem",
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#10b981",
    border: "none",
    borderRadius: "8px",
    transition: "background-color 0.2s",
  },
  exampleContainer: {
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    padding: "1.5rem",
    border: "1px solid #e2e8f0",
  },
  exampleTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "0.75rem",
  },
  exampleText: {
    fontSize: "0.9rem",
    color: "#64748b",
    marginBottom: "1rem",
  },
  exampleButtons: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
    marginBottom: "1rem",
  },
  exampleButton: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "1.5rem",
    textAlign: "center",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
  },
  exampleIcon: {
    fontSize: "2rem",
    marginBottom: "0.5rem",
  },
  exampleSubtext: {
    fontSize: "0.75rem",
    color: "#94a3b8",
    marginTop: "0.25rem",
  },
  exampleNote: {
    fontSize: "0.8rem",
    color: "#94a3b8",
    margin: 0,
    fontStyle: "italic",
  },
};

export default LinkSettings;